import { useTaskStore } from '../stores/taskStore';
import { useHabitStore } from '../stores/habitStore';
import { useEventStore } from '../stores/eventStore';
import { useProjectStore } from '../stores/projectStore';
import { useQuestStore } from '../stores/questStore';
import { useProgramStore } from '../stores/programStore';
import { useRoutineStore } from '../stores/routineStore';
import type { ActionTypeId } from '../constants/achievementBadges';
import type { ActiveItem } from './capacityCore';

/**
 * WHAT ACTUALLY FREES CAPACITY, PER TYPE — the block modal's remedies.
 *
 * ⚠️⚠️ THE ONLY TEST FOR ENTRY HERE IS: **does running this reduce the number
 * that blocked?** Not "does the type have an archive button somewhere", not
 * "does the store expose a `complete…` action". Four plausible entries were
 * rejected on exactly that test, and every one of them would have shipped a
 * modal whose remedy did nothing:
 *
 *  · **`routineStore.completeRoutine`** records a RUN — it bumps `completedCount`
 *    and leaves the routine active. Offering it as "complete this to free a
 *    slot" would have been a button that provably cannot work.
 *  · **Programs** have no completing action at all: a Program is a DEFINITION and
 *    completion lives on its `ProgramInitiation` RUN (see `programProgress.ts`).
 *  · **Projects** are never marked complete by hand — `status: 'completed'` is
 *    reached through task/milestone progress. Archive is the real lever.
 *  · **Skills** get their `completedAt` from their requirements, so there is no
 *    "finish this skill" to offer either.
 *
 * ⚠️ Abilities and Skills therefore have NEITHER remedy. That is not an
 * oversight and must not be papered over with a delete button — the modal says
 * so plainly and offers the Inbox, which always works.
 *
 * ⚠️ Every `run` below WRITES to the user's account, and the completions among
 * them MINT XP. They are only ever called from an explicit per-item button.
 */
export interface CapacityEscape {
  /** Button verb. */
  label: string;
  /** One line on what it does to the record — shown above the list. */
  hint: string;
  run: (item: ActiveItem, domainId: string) => void;
  /**
   * Why this item cannot take the action right now, or `null` if it can.
   * ⚠️ A remedy offered on an item that will refuse it is the same lie as a
   * remedy that does nothing.
   */
  blockedReason?: (item: ActiveItem) => string | null;
}

export interface CapacityEscapes {
  complete?: CapacityEscape;
  archive?: CapacityEscape;
}

export const CAPACITY_ESCAPES: Record<ActionTypeId, CapacityEscapes> = {
  tasks: {
    complete: {
      label: 'Complete',
      hint: 'A completed task stops taking up a slot — and raises the allowance.',
      run: (item, domainId) => useTaskStore.getState().toggleTask(item.id, domainId),
    },
    // ⚠️ Tasks have no archive. `Task` carries no archived flag and taskStore has
    // no archive action — only delete, which the modal will not do for you.
  },

  habits: {
    // ⚠️⚠️ NO COMPLETE, BY NATURE. A habit is never "done" — completing one is a
    // check-in for a DATE and it keeps its slot forever after. Offering it here
    // would be the clearest possible example of a remedy that cannot work.
    archive: {
      label: 'Archive',
      hint: 'Archiving keeps the habit and its whole history — it just stops holding a slot.',
      run: item => useHabitStore.getState().archiveHabit(item.id),
      // Prayer habits are managed by Prayer Settings and `archiveHabit` returns
      // early on them, so the button would silently do nothing.
      blockedReason: item =>
        useHabitStore.getState().habits.find(h => h.id === item.id)?.system
          ? 'Managed in Prayer Settings' : null,
    },
  },

  projects: {
    archive: {
      label: 'Archive',
      hint: 'Archiving keeps the project and everything in it — it just stops holding a slot.',
      run: item => useProjectStore.getState().archiveProject(item.id),
    },
  },

  events: {
    complete: {
      label: 'Complete',
      hint: 'A completed event stops taking up a slot — and raises the allowance.',
      run: item => useEventStore.getState().toggleEvent(item.id),
    },
  },

  quests: {
    complete: {
      label: 'Complete',
      hint: 'Completing a quest frees its slot and raises the allowance.',
      run: item => useQuestStore.getState().completeQuest(item.id),
      // ⚠️ `completeQuest` returns early unless EVERY goal is done. Without this
      // the button would look available and do nothing on most quests.
      blockedReason: item => {
        const q = useQuestStore.getState().quests.find(x => x.id === item.id);
        if (!q) return 'No longer available';
        const open = q.goals.filter(g => !g.completed).length;
        return open ? `${open} goal${open === 1 ? '' : 's'} still open` : null;
      },
    },
    archive: {
      label: 'Archive',
      hint: 'Archiving keeps the quest and its goals — it just stops holding a slot.',
      run: item => useQuestStore.getState().archiveQuest(item.id),
    },
  },

  goals: {
    complete: {
      label: 'Complete',
      hint: 'A completed goal stops taking up a slot in its quest.',
      // ⚠️ `parentId` is the owning quest. A goal has no store action of its own.
      run: item => { if (item.parentId) useQuestStore.getState().toggleGoal(item.parentId, item.id); },
    },
  },

  // ⚠️⚠️ NEITHER REMEDY EXISTS. An Ability has no completed, archived or done
  // state — it levels. Nothing short of deleting it frees the slot, and the
  // modal will not delete on the user's behalf.
  abilities: {},

  // ⚠️⚠️ NEITHER REMEDY EXISTS. A Skill's `completedAt` is derived from its
  // requirements, so there is no "finish this" to press, and Skills have no
  // archive.
  skills: {},

  programs: {
    archive: {
      label: 'Archive',
      hint: 'Archiving keeps the program and any runs of it — it just stops holding a slot.',
      run: item => useProgramStore.getState().setProgramArchived(item.id, true),
    },
  },

  routines: {
    // ⚠️⚠️ NO COMPLETE. `completeRoutine` records a RUN — see the header.
    archive: {
      label: 'Archive',
      hint: 'Archiving keeps the routine and its run history — it just stops holding a slot.',
      run: item => useRoutineStore.getState().updateRoutine(item.id, { archived: true }),
    },
  },
};
